import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * La Dama Helada (the Chasm's Frost Veins guardian, docs/SIMA.md «Guardianes»): «Si encierra a uno en hielo, los
 * demás rompen el hielo.» A lady of ice twice a man's height and far too thin: a long gown of old blue ice
 * flaring to a hem of icicles that never touches the floor, a waist a hand could close round, a high collar of
 * shards behind a small pale mask with dark hollow eyes, and a tall crown of icicles. Frozen veils hang from the
 * crown down her back and from her sleeves; long fingers of ice. Cold light in her eyes, her mouth, the seams of
 * her bodice; mist pooling under her hem.
 *
 * How the integrator drives her (anim/creatures/frost-lady.ts): 'breath' (2.0 s) the cone of frost, blowing from
 * 0.7 s to 1.6 s (the `breath` bone, built tiny); 'cast' (1.8 s) the ice: she gathers a star of frost in her
 * right hand overhead (the `spell` bone, built tiny, 0.2–0.85 s) and thrusts it at the hero (0.95 s), closing her
 * fist as the ice takes him.
 *
 * Bones: body (the gown, from the hips), chest, head, `breath`, arms in three (arm, fore, hand), `spell` (in the
 * right hand). Faces +Z with her `R` arm on +X. Opaque shared materials; the crystal and the veils translucent,
 * the mist unlit; glow sprites, no lights.
 */

/** How small the breath cone and the spell star are built (their clips scale them by the inverse). */
export const FROST_LADY_REST = 0.02;
export const FROST_LADY_HEIGHT = 3.0;

export const frostLady = (o?: BaseOpts): Model => {
  const c = begin(o, 0.9, 7781);
  const { g, rng } = c;
  const k4 = kit(rng);

  const iceD = std('#24435e', { roughness: 0.3, metalness: 0.15, flatShading: true, side: THREE.DoubleSide });
  const ice = std('#7fa9c8', { roughness: 0.22, metalness: 0.1, flatShading: true });
  const frost = std('#e2eef7', { roughness: 0.85, flatShading: true });
  const crystal = emis('#d6eeff', '#5aa8e0', 0.6, { transparent: true, opacity: 0.8, roughness: 0.1, flatShading: true });
  const veil = std('#cfe8ff', { transparent: true, opacity: 0.38, roughness: 0.4, side: THREE.DoubleSide, depthWrite: false, emissive: '#5b87ad', emissiveIntensity: 0.25 });
  const cold = emis('#f6fcff', '#9fdcff', 2.4);
  const mist = basic('#d6ecfa', 0.3);

  /* ---------- the gown: flaring to a jagged hem of icicles, pleated ---------- */
  const bodyB = joint(g, V(0, 1.3, 0), 'body');
  const body = bodyB.abs;
  {
    const geo = new THREE.CylinderGeometry(0.17, 0.74, 1.2, 18, 4, true);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const a = Math.atan2(z, x);
      const lo = (0.6 - y) / 1.2;
      const pleat = 1 + 0.08 * lo * Math.sin(a * 9);
      let yy = y;
      if (y < -0.59) yy += (Math.round((a + Math.PI) * 18 / (Math.PI * 2)) % 2 ? 0.16 : -0.12);
      pos.setXYZ(i, x * pleat, yy, z * pleat * (z < 0 ? 1.12 : 1));
    }
    geo.computeVertexNormals();
    const gown = M(geo, iceD, body);
    gown.position.set(0, 0.78, -0.03);
  }
  // A paler over-gown at the front, split down the middle.
  for (const k of [-1, 1]) {
    const p = k4.panel(body, V(k * 0.12, 1.33, 0.12), 0.34, 1.12, 0.12, 1, ice, 0.25);
    p.rotation.set(-0.38, k * 0.42, 0);
  }
  // Ribs of crystal down the gown, and a crust of frost along the hem.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.17;
    const top = V(Math.cos(a) * 0.19, 1.32, Math.sin(a) * 0.19 - 0.03);
    const bot = V(Math.cos(a) * 0.78, 0.12, Math.sin(a) * 0.78 * (Math.sin(a) < 0 ? 1.12 : 1) - 0.03);
    k4.crack(body, [top, top.clone().lerp(bot, 0.5).multiply(V(1.02, 1, 1.02)), bot], 0.014, i % 3 ? crystal : cold, 0.02);
  }
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    k4.lump(body, frost, Math.cos(a) * 0.74, 0.1, Math.sin(a) * 0.74 * (Math.sin(a) < 0 ? 1.12 : 1) - 0.03, 0.09, 0.04, 0.08, 0.4, 0);
  }
  k4.lump(body, frost, 0, 1.35, 0, 0.2, 0.06, 0.15, 0.3, 0);
  // Mist pooling under her hem.
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    glowSprite(body, '#cfe8ff', 1.0, V(Math.cos(a) * 0.5, 0.12, Math.sin(a) * 0.5), 0.2);
  }
  const pool = M(new THREE.CircleGeometry(0.85, 18), mist, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.02;

  /* ---------- the bodice: a waist a hand could close round, ribs of ice, seams of cold light ---------- */
  const chestB = joint(body, V(0, 1.4, 0), 'chest');
  const chest = chestB.abs;
  {
    const geo = new THREE.LatheGeometry([[0.13, 1.32], [0.15, 1.5], [0.21, 1.75], [0.23, 1.92], [0.17, 2.05], [0.06, 2.1]].map(([r, y]) => new THREE.Vector2(r!, y!)), 10);
    geo.scale(1, 1, 0.68);
    M(geo, ice, chest);
  }
  for (let i = 0; i < 4; i++) {
    const y = 1.55 + i * 0.1;
    for (const k of [-1, 1]) k4.shard(chest, frost, V(k * 0.04, y, 0.13), V(k * 1, -0.5, 0.3), 0.18 - i * 0.015, 0.02);
  }
  k4.crack(chest, [V(0, 1.4, 0.11), V(0.01, 1.6, 0.14), V(-0.01, 1.8, 0.16), V(0, 1.98, 0.13)], 0.012, cold, 0.02);
  glowSprite(chest, '#9fdcff', 0.5, V(0, 1.75, 0.2), 0.35);
  // The high collar: a fan of shards behind her neck.
  for (let i = 0; i < 9; i++) {
    const a = (i - 4) * 0.28;
    k4.shard(chest, crystal, V(Math.sin(a) * 0.18, 2.0, -0.08 - Math.cos(a) * 0.04), V(Math.sin(a) * 1.1, 1, -0.35), 0.36 - Math.abs(i - 4) * 0.035, 0.04);
  }
  // The mantle: a veil of ice from her shoulders, flaring down behind her to the floor.
  {
    const geo = new THREE.CylinderGeometry(0.24, 1.0, 1.95, 14, 3, true, Math.PI * 0.5, Math.PI);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) if (pos.getY(i) < -0.97) pos.setY(i, pos.getY(i) + (i % 2 ? 0.12 : 0) + rng() * 0.05);
    geo.computeVertexNormals();
    const mantle = M(geo, veil, chest);
    mantle.position.set(0, 1.04, -0.08);
    mantle.scale.set(1, 1, 0.75);
  }
  // Shoulders: points of ice.
  for (const k of [-1, 1]) k4.shard(chest, ice, V(k * 0.24, 2.0, 0), V(k, 0.5, 0), 0.16, 0.06);

  /* ---------- head: a small pale mask, hollow eyes, a crown of icicles, the veil behind ---------- */
  const headB = joint(chest, V(0, 2.12, 0.0), 'head');
  const head = headB.abs;
  limb(head, V(0, 2.06, 0), V(0, 2.2, 0.02), 0.045, 0.04, ice, 6);
  const mask = M(new THREE.SphereGeometry(1, 10, 8), frost, head);
  mask.position.set(0, 2.3, 0.04);
  mask.scale.set(0.105, 0.15, 0.115);
  // The chin: long and pointed.
  const chin = M(new THREE.ConeGeometry(0.05, 0.12, 5), frost, head);
  chin.position.set(0, 2.17, 0.07);
  chin.rotation.x = Math.PI + 0.3;
  for (const s of [-1, 1]) {
    const hol = M(new THREE.SphereGeometry(0.032, 6, 4), iceD, head);
    hol.position.set(s * 0.045, 2.32, 0.135);
    hol.scale.set(1.3, 0.75, 0.6);
    const eye = M(new THREE.SphereGeometry(0.012, 5, 3), cold, head);
    eye.position.set(s * 0.045, 2.32, 0.15);
    glowSprite(head, '#bfeaff', 0.1, V(s * 0.045, 2.32, 0.17), 0.8);
  }
  const mouth = M(new THREE.BoxGeometry(0.05, 0.008, 0.01), iceD, head);
  mouth.position.set(0, 2.22, 0.15);
  // The crown: a circlet and icicles fanning up, the middle ones tallest.
  const circ = M(new THREE.TorusGeometry(0.11, 0.015, 4, 14), ice, head);
  circ.position.set(0, 2.4, 0.03);
  circ.rotation.x = Math.PI / 2 - 0.15;
  for (let i = 0; i < 9; i++) {
    const a = (i - 4) * 0.36;
    const len = 0.6 - Math.abs(i - 4) * 0.09;
    k4.shard(head, crystal, V(Math.sin(a) * 0.11, 2.4, 0.03 + Math.cos(a) * 0.04 - 0.02), V(Math.sin(a) * 0.55, 1, -0.12), len, 0.03);
  }
  k4.shard(head, crystal, V(0, 2.4, -0.08), V(0, 1, -0.4), 0.42, 0.03);
  glowSprite(head, '#cfe8ff', 0.8, V(0, 2.62, 0), 0.22);
  // The veil: from the crown down her back nearly to the floor.
  const vl = k4.panel(head, V(0, 2.42, -0.12), 0.5, 1.1, 0.2, -1, veil, 0.35);
  vl.rotation.x = 0.12;

  // The breath: a cone of frost from the mouth, forward and down (built tiny; see FROST_LADY_REST).
  const breathB = joint(head, V(0, 2.22, 0.16), 'breath');
  {
    const L = 2.4 * FROST_LADY_REST, R = 0.8 * FROST_LADY_REST;
    const bg = G(breathB.abs, 0, 2.22, 0.16);
    bg.rotation.x = Math.PI / 2 + 0.42;
    const outer = new THREE.ConeGeometry(R, L, 9, 1, true);
    outer.translate(0, -L / 2, 0);
    outer.rotateX(Math.PI);
    M(outer, mist, bg);
    const inner = new THREE.ConeGeometry(R * 0.45, L * 0.8, 7, 1, true);
    inner.translate(0, -L * 0.4, 0);
    inner.rotateX(Math.PI);
    M(inner, crystal, bg);
  }

  /* ---------- arms: long and thin, sleeves of veil, fingers of ice ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  let spellB: { p: THREE.Group; abs: THREE.Group } | undefined;
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(chest, V(k * 0.24, 1.98, 0), `arm${s}`);
    const sh = armB.abs;
    limb(sh, V(k * 0.24, 1.98, 0), V(k * 0.32, 1.6, 0.04), 0.05, 0.04, ice, 6);
    const foreB = joint(sh, V(k * 0.32, 1.6, 0.04), `fore${s}`);
    const fo = foreB.abs;
    limb(fo, V(k * 0.32, 1.6, 0.04), V(k * 0.37, 1.2, 0.1), 0.04, 0.032, ice, 6);
    k4.shard(fo, frost, V(k * 0.33, 1.56, 0.0), V(k * 0.3, 0.2, -1), 0.14, 0.025);
    // The sleeve: a veil hanging from the forearm.
    const sl = k4.panel(fo, V(k * 0.35, 1.5, 0.05), 0.26, 0.8, 0.08, k, veil, 0.4);
    sl.rotation.y = k * Math.PI / 2;
    const handB = joint(fo, V(k * 0.37, 1.2, 0.1), `hand${s}`);
    const ha = handB.abs;
    const palm = M(new THREE.SphereGeometry(1, 6, 4), frost, ha);
    palm.position.set(k * 0.38, 1.16, 0.11);
    palm.scale.set(0.035, 0.055, 0.025);
    for (let i = 0; i < 4; i++) {
      k4.shard(ha, crystal, V(k * (0.36 + i * 0.012), 1.12, 0.1 + (i - 1.5) * 0.02), V(k * 0.1, -1, 0.2 + (i - 1.5) * 0.15), 0.24 - Math.abs(i - 1.5) * 0.03, 0.012);
    }
    if (k > 0) {
      // The star of frost she gathers to cast (built tiny).
      spellB = joint(ha, V(0.39, 1.1, 0.16), 'spell');
      const sg = G(spellB.abs, 0.39, 1.1, 0.16);
      sg.scale.setScalar(FROST_LADY_REST);
      for (let j = 0; j < 6; j++) {
        const a = (j / 6) * Math.PI * 2;
        k4.shard(sg, crystal, V(0, 0, 0), V(Math.cos(a), Math.sin(a), 0.2), 0.32, 0.05);
        k4.shard(sg, frost, V(Math.cos(a) * 0.14, Math.sin(a) * 0.14, 0), V(Math.cos(a + 0.6), Math.sin(a + 0.6), 0), 0.1, 0.02);
      }
      M(new THREE.OctahedronGeometry(0.07, 0), cold, sg);
      glowSprite(sg, '#bfeaff', 0.9, V(0, 0, 0.02), 0.8);
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
    arms[`hand${s}`] = handB.p;
  }

  return done(c, FROST_LADY_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, breath: breathB.p,
    armL: arms.armL!, foreL: arms.foreL!, handL: arms.handL!, armR: arms.armR!, foreR: arms.foreR!, handR: arms.handR!, spell: spellB!.p,
  }));
};
